import React, { useEffect, useState, useCallback } from "react";
import { Navigate, Link } from "react-router-dom";
import { Plus, Pencil, Trash2, LogOut, ExternalLink, Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { http, listContent, uploadMedia } from "@/lib/api";
import { TYPES, NAV_ORDER, linkTo, getTitle, resolveMedia } from "@/config";

const NAME_KEY = { people: "name", museums: "name" };
const IMAGE_KEY = { cases: "hero_image" };
const LEAD_KEY = { cases: "summary", news: "dek", essays: "dek", people: "bio", museums: "description", artworks: "description", episodes: "description" };
const BODY_KEY = { cases: "body", news: "body", essays: "body", episodes: "show_notes", people: "bio" };

const emptyForm = { title: "", slug: "", subtitle: "", category: "", image: "", image_credit: "", image_position: "", website: "", lead: "", body: "", tags: "", featured: false, json: "" };

const POSITION_OPTIONS = [
  ["", "Default (centered)"],
  ["center top", "Top (best for portraits / heads)"],
  ["center 25%", "Upper"],
  ["center", "Center"],
  ["center 75%", "Lower"],
  ["center bottom", "Bottom"],
  ["left center", "Left"],
  ["right center", "Right"],
];

export default function AdminDashboard() {
  const { user, ready, logout } = useAuth();
  const [ctype, setCtype] = useState("cases");
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // item or {} for new
  const [form, setForm] = useState(emptyForm);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const { url } = await uploadMedia(file);
      setForm((f) => ({ ...f, image: url }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const load = useCallback((c) => {
    listContent(c, { limit: 500 }).then(setItems).catch(() => setItems([]));
  }, []);

  useEffect(() => { if (user) load(ctype); }, [ctype, user, load]);

  if (!ready) return <div className="px-5 py-32 eyebrow">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;

  const nameKey = NAME_KEY[ctype] || "title";
  const imageKey = IMAGE_KEY[ctype] || "image";
  const leadKey = LEAD_KEY[ctype] || "summary";
  const bodyKey = BODY_KEY[ctype] || "body";

  const openNew = () => { setForm(emptyForm); setEditing({}); };
  const openEdit = (it) => {
    const { id, slug, created_at, updated_at, ...rest } = it;
    const known = new Set([nameKey, "slug", "subtitle", "category", imageKey, "image_credit", "image_position", "website", leadKey, bodyKey, "tags", "featured"]);
    const extra = Object.fromEntries(Object.entries(rest).filter(([k]) => !known.has(k)));
    setForm({
      title: it[nameKey] || "",
      slug: it.slug || "",
      subtitle: it.subtitle || "",
      category: it.category || "",
      image: it[imageKey] || "",
      image_credit: it.image_credit || "",
      image_position: it.image_position || "",
      website: it.website || "",
      lead: it[leadKey] || "",
      body: it[bodyKey] || "",
      tags: (it.tags || []).join(", "),
      featured: !!it.featured,
      json: Object.keys(extra).length ? JSON.stringify(extra, null, 2) : "",
    });
    setEditing(it);
  };

  const save = async () => {
    let extra = {};
    if (form.json.trim()) {
      try { extra = JSON.parse(form.json); }
      catch { toast.error("Advanced JSON is invalid."); return; }
    }
    const payload = {
      [nameKey]: form.title,
      subtitle: form.subtitle || undefined,
      category: form.category || undefined,
      [imageKey]: form.image || undefined,
      image_credit: form.image_credit.trim(),
      image_position: form.image_position || undefined,
      website: form.website || undefined,
      [leadKey]: form.lead || undefined,
      [bodyKey]: form.body || undefined,
      tags: form.tags ? form.tags.split(",").map((t) => t.trim()).filter(Boolean) : undefined,
      featured: form.featured,
      ...(form.slug ? { slug: form.slug } : {}),
      ...extra,
    };
    try {
      if (editing.id) {
        await http.put(`/admin/content/${ctype}/${editing.id}`, payload);
        toast.success("Entry updated.");
      } else {
        await http.post(`/admin/content/${ctype}`, payload);
        toast.success("Entry created.");
      }
      setEditing(null);
      load(ctype);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed.");
    }
  };

  const remove = async (it) => {
    if (!window.confirm(`Delete “${getTitle(it)}”?`)) return;
    await http.delete(`/admin/content/${ctype}/${it.id}`);
    toast.success("Entry deleted.");
    load(ctype);
  };

  return (
    <div className="mx-auto max-w-[1400px] px-5 lg:px-10 py-14">
      <div className="flex items-center justify-between border-b border-line pb-6">
        <div>
          <div className="eyebrow mb-2 text-gold">Editor’s Desk</div>
          <h1 className="font-serif text-4xl text-parchment">Content Management</h1>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-faint">{user.email}</span>
          <button data-testid="logout-button" onClick={logout} className="inline-flex items-center gap-2 eyebrow text-faint hover:text-gold">
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </div>

      <div className="mt-8 flex flex-wrap gap-2" data-testid="admin-type-tabs">
        {NAV_ORDER.map((k) => (
          <button
            key={k}
            data-testid={`admin-tab-${k}`}
            onClick={() => setCtype(k)}
            className={`border px-4 py-2 eyebrow transition-colors ${
              ctype === k ? "border-gold bg-gold/10 text-gold" : "border-line text-faint hover:text-parchment"
            }`}
          >
            {TYPES[k].label}
          </button>
        ))}
      </div>

      <div className="mt-8 flex items-center justify-between">
        <div className="eyebrow">{items.length} {TYPES[ctype].label}</div>
        <button data-testid="new-entry-button" onClick={openNew}
          className="inline-flex items-center gap-2 bg-gold px-5 py-2.5 text-xs font-semibold uppercase tracking-widest text-ink hover:bg-gold-hover">
          <Plus size={15} /> New {TYPES[ctype].singular}
        </button>
      </div>

      <div className="mt-6 border border-line" data-testid="admin-list">
        {items.map((it) => (
          <div key={it.id} className="flex items-center gap-4 border-b border-line px-4 py-3 last:border-b-0 hover:bg-surface">
            <div className="min-w-0 flex-1">
              <div className="font-serif text-lg text-parchment truncate">{getTitle(it)}</div>
              <div className="font-mono text-xs text-faint truncate">/{it.slug}</div>
            </div>
            {it.featured && <span className="eyebrow text-gold">Featured</span>}
            <Link to={linkTo(ctype, it.slug)} target="_blank" className="p-2 text-faint hover:text-gold" title="View">
              <ExternalLink size={16} />
            </Link>
            <button data-testid={`edit-${it.slug}`} onClick={() => openEdit(it)} className="p-2 text-faint hover:text-gold" title="Edit">
              <Pencil size={16} />
            </button>
            <button data-testid={`delete-${it.slug}`} onClick={() => remove(it)} className="p-2 text-faint hover:text-blood" title="Delete">
              <Trash2 size={16} />
            </button>
          </div>
        ))}
        {items.length === 0 && <div className="px-4 py-10 text-center font-serif text-xl text-faint">No entries.</div>}
      </div>

      {editing && (
        <div className="fixed inset-0 z-[80] flex items-start justify-center overflow-y-auto bg-ink/85 backdrop-blur-md p-4 py-12" data-testid="editor-modal">
          <div className="w-full max-w-2xl border border-line bg-surface p-8">
            <h2 className="font-serif text-3xl text-parchment">
              {editing.id ? "Edit" : "New"} {TYPES[ctype].singular}
            </h2>
            <div className="mt-6 space-y-4">
              <Field label={nameKey === "name" ? "Name" : "Title"}>
                <input data-testid="form-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="inp" />
              </Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Slug (optional)">
                  <input data-testid="form-slug" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="auto-generated" className="inp" />
                </Field>
                <Field label="Category">
                  <input data-testid="form-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="inp" />
                </Field>
              </div>
              <Field label="Subtitle">
                <input value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} className="inp" />
              </Field>
              <Field label="Image URL">
                <input data-testid="form-image" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} className="inp" />
                <div className="mt-2 flex items-center gap-3">
                  <label
                    data-testid="form-image-upload"
                    className={`inline-flex cursor-pointer items-center gap-2 border border-gold/50 px-4 py-2 text-xs uppercase tracking-widest text-gold transition-colors hover:bg-gold hover:text-ink ${uploading ? "pointer-events-none opacity-60" : ""}`}
                  >
                    {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                    {uploading ? "Uploading…" : "Upload image"}
                    <input type="file" accept="image/*" className="hidden" onChange={handleUpload} disabled={uploading} />
                  </label>
                  {form.image && (
                    <img src={resolveMedia(form.image)} alt="preview" className="h-12 w-16 border border-line object-cover" />
                  )}
                </div>
              </Field>
              <Field label="Photo credit (shown under the image)">
                <input
                  data-testid="form-image-credit"
                  value={form.image_credit}
                  onChange={(e) => setForm({ ...form, image_credit: e.target.value })}
                  placeholder="Photo: Jane Doe / Wikimedia Commons, CC BY-SA 4.0"
                  className="inp"
                />
              </Field>
              <Field label="Website URL (shown as a clickable “Official website” link)">
                <input
                  data-testid="form-website"
                  value={form.website}
                  onChange={(e) => setForm({ ...form, website: e.target.value })}
                  placeholder="https://www.example.com"
                  className="inp"
                />
              </Field>
              <Field label="Image placement (crop focus)">
                <div className="flex gap-4">
                  <select
                    data-testid="form-image-position"
                    value={form.image_position}
                    onChange={(e) => setForm({ ...form, image_position: e.target.value })}
                    className="inp flex-1"
                  >
                    {POSITION_OPTIONS.map(([v, label]) => (
                      <option key={v} value={v} className="bg-ink text-parchment">{label}</option>
                    ))}
                  </select>
                  {form.image && (
                    <div className="h-24 w-32 shrink-0 overflow-hidden border border-line bg-elevated">
                      <img src={resolveMedia(form.image)} alt="preview" className="h-full w-full object-cover"
                        style={{ objectPosition: form.image_position || "center" }} />
                    </div>
                  )}
                </div>
              </Field>
              <Field label="Lead / Summary">
                <textarea data-testid="form-lead" rows={2} value={form.lead} onChange={(e) => setForm({ ...form, lead: e.target.value })} className="inp resize-y" />
              </Field>
              <Field label="Body (blank line between paragraphs)">
                <textarea data-testid="form-body" rows={6} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="inp resize-y" />
              </Field>
              <Field label="Tags (comma separated)">
                <input data-testid="form-tags" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} className="inp" />
              </Field>
              <Field label="Advanced fields (JSON — e.g. people_ids, artwork_ids)">
                <textarea rows={3} value={form.json} onChange={(e) => setForm({ ...form, json: e.target.value })} className="inp font-mono text-xs resize-y" placeholder='{"people_ids": ["p_peruggia"]}' />
              </Field>
              <label className="flex items-center gap-3">
                <input type="checkbox" data-testid="form-featured" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="h-4 w-4 accent-[#D4AF37]" />
                <span className="eyebrow">Featured</span>
              </label>
            </div>
            <div className="mt-8 flex justify-end gap-3">
              <button onClick={() => setEditing(null)} className="border border-line px-5 py-2.5 eyebrow text-faint hover:text-parchment">Cancel</button>
              <button data-testid="save-entry" onClick={save} className="bg-gold px-6 py-2.5 text-xs font-semibold uppercase tracking-widest text-ink hover:bg-gold-hover">Save</button>
            </div>
          </div>
        </div>
      )}

      <style>{`.inp{width:100%;background:#0A0A0C;border:1px solid #23252E;padding:0.6rem 0.85rem;color:#EAE6D7;font-size:0.9rem}.inp:focus{outline:none;border-color:#D4AF37}`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="eyebrow mb-2 block">{label}</label>
      {children}
    </div>
  );
}
